import React, { useEffect, useState } from "react";
import styles from "./chat-input-section.module.css";
import CustomTextfield from "../../../../../../../../common/input/custom-textfield/custom-textfield";
import { Button, Spin, Upload } from "antd";
import { PaperClipOutlined, SendOutlined } from "@ant-design/icons";
import { MAX_FILES, MAX_TOTAL_SIZE } from "../../../../../../../../constants/common";
import { toast } from "react-toastify";
import AttachmentPreviewSection from "./attachment-preview-section/attachment-preview-section";
import { useChat } from "../../../../../../../../context/chat-context";

const ChatInputSection = (props) => {
  const {
    disableChat,
    bottomRef,
    onSend,
  } = props;
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const { receiverIdRef } = useChat();
  let uploadErrorShown = false;

  useEffect(() => {
    setText("");
    setAttachments([]);
  }, [receiverIdRef.current]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "auto" });
  }, [attachments]);

  useEffect(() => {
    return () => {
      attachments?.forEach((file) => {
        if (file?.originFileObj) URL.revokeObjectURL(file?.preview);
      });
    };
  }, []);

  const handleSend = async () => {
    if (!text.trim() && attachments?.length === 0) return;
    try {
      setSending(true);
      const result = await onSend(text.trim(), attachments);
      if (result) {
        setText("");
        setAttachments([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  const beforeUpload = (file, fileList) => {
    if (uploadErrorShown) return Upload.LIST_IGNORE;

    const isDuplicate = attachments?.some(
      (f) => f?.name === file?.name && f?.size === file?.size
    );
    if (isDuplicate) {
      showUploadError(`Duplicate files are not allowed.`);
      return Upload.LIST_IGNORE;
    }

    const totalCount = attachments?.length + fileList?.length;
    if (totalCount > MAX_FILES) {
      showUploadError(`You can only upload up to ${MAX_FILES} files.`);
      return Upload.LIST_IGNORE;
    }

    const totalSize =
      attachments?.reduce((acc, f) => acc + (f?.size || 0), 0) +
      fileList?.reduce((acc, f) => acc + (f?.size || 0), 0);

    if (totalSize > MAX_TOTAL_SIZE) {
      showUploadError(`Overall size cannot exceed ${MAX_TOTAL_SIZE / (1024 * 1024)} MB.`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const showUploadError = (error) => {
    toast.error(error);
    uploadErrorShown = true;
    setTimeout(() => (uploadErrorShown = false), 0);
  };

  const handleAttachmentChange = ({fileList}) => {
    const newFiles = fileList?.map((file) => ({
      ...file,
      preview: file?.originFileObj ? URL.createObjectURL(file?.originFileObj) : null,
    }));
    setAttachments(newFiles);
  };

  const removeAttachment = (file) => {
    setAttachments((prev) => prev?.filter((f) => f?.uid !== file?.uid));
  };

  const handleInputChange = (e) => {
    setText(e.target.value)
  }

  return (
    <>
      {attachments?.length > 0 && (
        <AttachmentPreviewSection
          attachments={attachments}
          removeAttachment={removeAttachment}
        />
      )}
      <div className={styles["chat-input-area"]}>
        <div className={styles["chat-input-container"]}>
          <CustomTextfield
            type="text"
            value={text}
            onChange={handleInputChange}
            placeholder="Type message"
            onPressEnter={handleSend}
            disabled={disableChat}
            suffix={
              <Upload
                multiple
                showUploadList={false}
                beforeUpload={beforeUpload}
                fileList={attachments}
                onChange={handleAttachmentChange}
              >
                <PaperClipOutlined className={styles["chat-icon"]} />
              </Upload>
            }
          />
        </div>
        {sending ? (
          <Button
            className={styles["chat-send-btn"]}
            disabled={true}
          >
            <Spin size="small" />
          </Button>
        ) : (
          <Button
            onClick={handleSend}
            className={styles["chat-send-btn"]}
            disabled={disableChat || (!text.trim() && attachments?.length === 0)}
          >
            <SendOutlined className={styles["chat-send-icon"]} />
          </Button>
        )}
      </div>
    </>
  );
};

export default ChatInputSection;